import { FC } from 'react';
import {
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans } from '@libs/i18n';

interface AssessmentsTableSkeletonProps {
  /** Number of placeholder rows to render. Matches the page size visually. */
  rowCount?: number;
}

const HEADER_KEYS = [
  'app.assessments.table.name',
  'app.assessments.table.status',
  'app.assessments.table.progress',
  'app.assessments.table.score',
  'app.assessments.table.submitted',
  'app.assessments.table.completed',
  'app.assessments.table.created',
  'app.assessments.table.updated',
  'app.assessments.table.actions',
] as const;

/**
 * Loading placeholder for the assessments list. Mirrors AssessmentsTable's
 * column layout 1:1 so swapping it in/out doesn't cause a width jump. Renders
 * Skeletons for each cell — notably a flat grey bar for the progress column,
 * which avoids the "0% → score" animation users see when MUI's determinate
 * LinearProgress mounts with cold-cache data.
 */
export const AssessmentsTableSkeleton: FC<AssessmentsTableSkeletonProps> = observer(({ rowCount = 8 }) => {
  const theme = useTheme();

  return (
    <TableContainer
      component={Paper}
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.grey[300]}`,
        boxShadow: 'none',
        overflow: 'auto',
      }}
    >
      <Table sx={{ width: '100%', minWidth: 1180, tableLayout: 'fixed' }} stickyHeader>
        <colgroup>
          <col style={{ width: '24%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '7%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <TableHead>
          <TableRow>
            {HEADER_KEYS.map((key, idx) => (
              <TableCell
                key={key}
                sx={{
                  backgroundColor: theme.palette.grey[200],
                  paddingY: 1.5,
                  paddingLeft: idx === 0 ? 2 : 1,
                  paddingRight: idx === HEADER_KEYS.length - 1 ? 3 : 1,
                  textAlign: 'start',
                  borderBottom: `1px solid ${theme.palette.grey[300]}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: theme.palette.text.secondary,
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase',
                  }}
                >
                  <Trans i18nKey={key} />
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: rowCount }).map((_, rowIdx) => (
            <TableRow key={rowIdx}>
              {/* Name: title + small subtitle */}
              <TableCell sx={{ py: 2.25, pl: 2 }}>
                <Skeleton variant="text" width="70%" height={18} />
                <Skeleton variant="text" width="40%" height={14} />
              </TableCell>
              {/* Status: badge */}
              <TableCell sx={{ py: 2.25 }}>
                <Skeleton variant="rounded" width={84} height={22} sx={{ borderRadius: 999 }} />
              </TableCell>
              {/* Progress: flat bar + percentage — no animation, no 0→score sweep */}
              <TableCell sx={{ py: 2.25 }}>
                <Skeleton variant="rounded" width="100%" height={8} sx={{ borderRadius: 999 }} />
              </TableCell>
              {/* Score: small pill */}
              <TableCell sx={{ py: 2.25 }}>
                <Skeleton variant="rounded" width={48} height={22} sx={{ borderRadius: 999 }} />
              </TableCell>
              {/* Submitted */}
              <TableCell sx={{ py: 2.25 }}>
                <Skeleton variant="text" width="80%" height={16} />
              </TableCell>
              {/* Completed */}
              <TableCell sx={{ py: 2.25 }}>
                <Skeleton variant="text" width="80%" height={16} />
              </TableCell>
              {/* Created */}
              <TableCell sx={{ py: 2.25 }}>
                <Skeleton variant="text" width="80%" height={16} />
              </TableCell>
              {/* Updated */}
              <TableCell sx={{ py: 2.25 }}>
                <Skeleton variant="text" width="80%" height={16} />
              </TableCell>
              {/* Actions: button + kebab */}
              <TableCell sx={{ py: 2.25, pr: 3 }}>
                <Skeleton variant="rounded" width="70%" height={28} sx={{ borderRadius: 1.5 }} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
